import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { createCheckout } from "./api";
import { useI18n } from "./i18n";

const CartCtx = createContext(null);
const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

export const toLine = (product, variant, quantity = 1) => ({
  variant_id: variant.id, handle: product.handle, title: product.title, brand: product.brand,
  variant_title: variant.title === "Default Title" ? "" : variant.title, image: product.images[0],
  price: variant.price, compare_at_price: variant.compare_at_price, quantity,
});

export const CartProvider = ({ children }) => {
  const { t } = useI18n();
  const [items, setItems] = useState(() => load("ajs_cart", []));
  const [open, setOpen] = useState(false);
  const [discount, setDiscount] = useState(() => {
    const d = localStorage.getItem("ajs_discount") || "";
    if (/^GIFT\d+$/i.test(d)) { localStorage.removeItem("ajs_discount"); return ""; }
    return d;
  });
  const [redirecting, setRedirecting] = useState(false);
  const [wrap, setWrap] = useState(() => load("ajs_wrap", { on: false, message: "" }));
  useEffect(() => { localStorage.setItem("ajs_wrap", JSON.stringify(wrap)); }, [wrap]);

  useEffect(() => { localStorage.setItem("ajs_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { if (discount) localStorage.setItem("ajs_discount", discount); }, [discount]);

  const add = useCallback((line, openDrawer = true, notify = true) => {
    setItems((cur) => {
      const ex = cur.find((i) => i.variant_id === line.variant_id);
      if (ex) return cur.map((i) => (i === ex ? { ...i, quantity: Math.min(99, i.quantity + line.quantity) } : i));
      return [...cur, line];
    });
    if (openDrawer) setOpen(true);
    if (notify) toast.success(t("added_to_bag"));
  }, [t]);

  const setQty = (id, q) => setItems((cur) => (q < 1 ? cur.filter((i) => i.variant_id !== id) : cur.map((i) => (i.variant_id === id ? { ...i, quantity: q } : i))));
  const remove = (id) => setItems((cur) => cur.filter((i) => i.variant_id !== id));

  const checkout = async (lines = items, code, extras = {}) => {
    if (!lines.length) return;
    setRedirecting(true);
    try {
      const url = await createCheckout(lines.map(({ variant_id, quantity }) => ({ variant_id, quantity })), code || discount || undefined, extras);
      window.location.href = url;
    } catch {
      toast.error(t("checkout_error"));
      setRedirecting(false);
    }
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, open, setOpen, add, setQty, remove, checkout, subtotal, count, discount, setDiscount, redirecting, wrap, setWrap }}>
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
